import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import { DICTIONARY_LINKS } from '../../constants/seoDictionaryLinks';
import '../../styles/design-system/index.css';

const AiWorksheetGuidePage = () => {
  const navigate = useNavigate();

  const faqData = [
    { q: '학습지 만들기 사이트를 찾고 있는데, 왓퀴즈가 맞나요?', a: '네, 왓퀴즈는 과목과 주제만 입력하면 OX, 단답형, 서술형 등 다양한 유형의 학습지를 AI로 자동 생성하는 교사 전용 학습지 만들기 사이트예요.' },
    { q: '어떤 과목의 학습지를 만들 수 있나요?', a: '국어, 수학, 영어, 과학, 사회 등 모든 교과의 학습지를 생성할 수 있어요. 특정 단원이나 교과서 PDF를 첨부하면 해당 내용 기반의 문제가 출제돼요.' },
    { q: '시험문제와 학습지의 차이는 뭔가요?', a: '왓퀴즈에서는 학습용 활동지(학습지)와 평가용 시험문제 모두 생성할 수 있어요. 용도에 따라 난이도와 문제 유형을 조정하시면 돼요.' },
    { q: '학습지를 이미지로 다운로드할 수 있나요?', a: '네, PRO 구독 시 깔끔한 레이아웃의 이미지 학습지로 다운로드할 수 있어요. 무료 사용자는 텍스트 인쇄용 학습지를 이용할 수 있어요.' },
    { q: '수행평가와 연계할 수 있나요?', a: '네, 학습지를 수행평가 자료로 활용하고, 왓퀴즈의 AI 수행평가 채점 기능으로 채점까지 자동화할 수 있어요.' },
  ];

  return (
    <>
      <Helmet>
        <title>AI 학습지 만들기 완벽 가이드 | 시험문제·퀴즈 자동 생성 방법 (2026)</title>
        <meta name="description" content="AI 학습지 만들기 방법을 단계별로 안내해요. OX 퀴즈, 단답형, 서술형 문제를 AI로 자동 생성. 학년별·과목별 예시, 학습지 만들기 사이트 비교 포함. 교사 전용 왓퀴즈 활용법." />
        <meta name="keywords" content="학습지 만들기, 학습지 만들기 사이트, AI 학습지 만들기, 시험문제 만들기, 퀴즈 만들기, 학습지 자동 생성, 학습지 만들기 방법, 수행평가 학습지, AI 문제 출제, 교사 학습지" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/ai-worksheet" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/ai-worksheet" />
        <meta property="og:title" content="AI 학습지 만들기 완벽 가이드 | 시험문제 자동 생성 방법" />
        <meta property="og:description" content="AI 학습지 만들기 방법 단계별 안내. OX, 서술형, 빈칸 채우기 등 자동 생성. 학습지 만들기 사이트 비교." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              { "@type": "Article", "headline": "AI 학습지 만들기 완벽 가이드", "description": "교사가 AI로 학습지를 자동 생성하는 실전 가이드", "author": { "@type": "Organization", "name": "왓퀴즈" }, "publisher": { "@type": "Organization", "name": "왓퀴즈", "logo": "https://whatquiz.co.kr/images/logo512.png" }, "datePublished": "2026-03-09", "dateModified": "2026-03-09" },
              { "@type": "FAQPage", "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) }
            ]
          })}
        </script>
      </Helmet>

      <div style={{ fontFamily: 'var(--whatquiz-font-family)', backgroundColor: '#fff', minHeight: '100vh' }}>
        <section style={{ padding: '80px 20px 60px', textAlign: 'center', background: 'linear-gradient(180deg, #f0fdf4 0%, #fff 100%)' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#dcfce7', color: '#15803d', borderRadius: '20px', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>2026년 최신 가이드</span>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: '800', color: '#111', lineHeight: '1.3', marginBottom: '20px' }}>AI 학습지 만들기 완벽 가이드</h1>
            <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7', marginBottom: '32px' }}>
              AI 학습지 만들기란, AI가 과목과 주제를 분석해 OX 퀴즈, 단답형, 서술형 등 다양한 유형의 학습지를 자동으로 생성하는 기능이에요.
              이 가이드에서는 학습지 만들기 사이트 비교부터 왓퀴즈 활용법까지 상세히 안내해요.
            </p>
            <button onClick={() => navigate('/ai-worksheet')} style={{ padding: '14px 28px', background: '#16a34a', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
              지금 바로 학습지 만들기 →
            </button>
          </div>
        </section>

        <article style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '16px' }}>학습지 만들기, 왜 AI를 써야 할까요?</h2>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '32px' }}>
            시험문제나 학습지를 직접 만들면 보통 1~2시간이 걸려요. 기출문제를 찾고, 난이도를 조절하고, 레이아웃을 정리하는 데 많은 시간이 소요돼요.
            AI 학습지 만들기를 활용하면 과목과 주제만 입력해서 3분 만에 학습지를 완성할 수 있어요.
          </p>

          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '16px' }}>왓퀴즈로 학습지 만드는 3단계</h2>

          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111', marginBottom: '12px' }}>1단계: 과목·주제 입력</h3>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '24px' }}>
            왓퀴즈 채팅에서 "학습지 생성" 카테고리를 선택하고, 과목, 학년, 단원 또는 주제를 입력해요.
            "초등 4학년 수학 분수 단원 학습지"처럼 구체적으로 입력하면 더 정확한 문제가 출제돼요.
          </p>

          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111', marginBottom: '12px' }}>2단계: 아웃라인 확인</h3>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '24px' }}>
            AI가 생성한 문제 구성을 미리보기에서 확인해요. 문제 유형(OX, 단답형, 서술형 등), 난이도, 문항 수를 조정할 수 있어요.
          </p>

          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111', marginBottom: '12px' }}>3단계: 다운로드</h3>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '32px' }}>
            텍스트 인쇄용 학습지는 무료로, 깔끔한 이미지 학습지는 PRO에서 다운로드할 수 있어요. 정답지도 함께 제공돼요.
          </p>

          <div style={{ background: '#f0fdf4', borderRadius: '16px', padding: '24px', marginBottom: '40px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#15803d', marginBottom: '12px' }}>활용 예시</h3>
            <ul style={{ fontSize: '15px', color: '#334155', lineHeight: '2', paddingLeft: '20px' }}>
              <li><strong>단원 정리 학습지:</strong> "중학교 1학년 과학, 힘과 운동 단원 정리 문제"</li>
              <li><strong>수행평가 학습지:</strong> "고등학교 국어, 논설문 쓰기 수행평가 자료"</li>
              <li><strong>OX 퀴즈:</strong> "초등 3학년 사회, 우리 지역 OX 퀴즈 20문제"</li>
              <li><strong>시험 대비:</strong> "중학교 2학년 수학, 일차함수 중간고사 대비 문제"</li>
            </ul>
          </div>

          <p style={{ fontSize: '15px', color: '#334155', lineHeight: '1.8', marginBottom: '32px' }}>
            단원의 성취기준을 먼저 확인하고 요청하면 수업 목표에 맞는 문제가 나와요.
            성취기준 원문과 해설, 수업·평가 예시는 <a href={DICTIONARY_LINKS.curriculum.href} style={{ color: '#15803d' }}>{DICTIONARY_LINKS.curriculum.label}</a>에서 볼 수 있어요.
          </p>

          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '24px', marginTop: '48px' }}>자주 묻는 질문</h2>
          {faqData.map((faq, idx) => (
            <div key={idx} style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>{faq.q}</h3>
              <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.7' }}>{faq.a}</p>
            </div>
          ))}
        </article>

        <section style={{ padding: '60px 20px', textAlign: 'center', background: 'linear-gradient(180deg, #f0fdf4 0%, #fff 100%)' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#111', marginBottom: '12px' }}>학습지, AI로 자동 생성해보세요</h2>
          <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px' }}>교사 인증 후 무료로 시작할 수 있어요</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/ai-worksheet')} style={{ padding: '14px 28px', background: '#16a34a', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>AI 학습지 만들기</button>
            <button onClick={() => navigate('/guide/ai-ppt')} style={{ padding: '14px 28px', background: '#fff', color: '#334155', fontSize: '16px', fontWeight: '600', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer' }}>PPT 가이드 보기</button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AiWorksheetGuidePage;
